import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MultiSelectFilter } from './components/MultiSelectFilter';
import { 
  Calendar,
  Download,
  FileText,
  DollarSign,
  Receipt,
  Building,
  CreditCard,
  ArrowUpDown,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Eye,
  Filter,
  Users,
  Zap,
  Home,
  Upload,
  Play,
  FileCheck,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { format } from 'date-fns';
import { PaymentTaskList } from './components/PaymentTaskList';
import { UrgentPaymentsView } from './components/UrgentPaymentsView';
import { cn } from '@/lib/utils';

export type PaymentStage = 'payment-ready' | 'funds-requested' | 'funds-received' | 'payment-initiated' | 'paid';

export default function TumPaymentReport() {
  const { currentWorkspace } = useWorkspace();
  const [activeStage, setActiveStage] = useState<PaymentStage>('payment-ready');
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedCommodities, setSelectedCommodities] = useState<string[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [selectedPaymentTaskId, setSelectedPaymentTaskId] = useState<string>('');
  const [showUrgent, setShowUrgent] = useState(false);

  // Fetch customers for filter
  const { data: customers = [] } = useQuery({
    queryKey: ['tum-customers', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('tum_customers')
        .select('id, customer_name')
        .eq('workspace_id', currentWorkspace.id)
        .eq('status', 'active')
        .order('customer_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch utility accounts for filter
  const { data: accounts = [] } = useQuery({
    queryKey: ['tum-utility-accounts', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('tum_utility_accounts')
        .select('id, account_number, commodity')
        .eq('workspace_id', currentWorkspace.id)
        .order('account_number', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch properties for filter
  const { data: properties = [] } = useQuery({
    queryKey: ['tum-properties', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('tum_properties')
        .select('id, property_name')
        .eq('workspace_id', currentWorkspace.id)
        .order('property_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Get unique commodities
  const commodities = useMemo(() => {
    const unique = new Set(accounts.map((acc: any) => acc.commodity).filter(Boolean));
    return Array.from(unique).sort();
  }, [accounts]);

  // Calculate overall insights based on filters
  const overallInsights = useMemo(() => {
    // These would come from actual payment task data filtered by selected filters
    // For demo, we'll use static data that would be filtered in real implementation
    const allStages = {
      'payment-ready': { tasks: 8, bills: 45, amount: 12500.50 },
      'funds-requested': { tasks: 5, bills: 32, amount: 8750.25 },
      'funds-received': { tasks: 6, bills: 38, amount: 10250.75 },
      'payment-initiated': { tasks: 3, bills: 18, amount: 5250.00 },
      'paid': { tasks: 2, bills: 12, amount: 3500.00 },
    };

    const totalTasks = Object.values(allStages).reduce((sum, s) => sum + s.tasks, 0);
    const totalBills = Object.values(allStages).reduce((sum, s) => sum + s.bills, 0);
    const totalAmount = Object.values(allStages).reduce((sum, s) => sum + s.amount, 0);
    
    const paymentReady = allStages['payment-ready'];
    const fundsRequested = allStages['funds-requested'];
    const fundsReceived = allStages['funds-received'];
    const paid = allStages['paid'];

    return {
      totalTasks,
      totalBills,
      totalAmount,
      paymentReady: paymentReady.tasks,
      fundsRequested: fundsRequested.tasks,
      fundsReceived: fundsReceived.tasks,
      paid: paid.tasks,
      paymentReadyBills: paymentReady.bills,
      fundsRequestedBills: fundsRequested.bills,
      fundsReceivedBills: fundsReceived.bills,
      paidBills: paid.bills,
      paymentReadyAmount: paymentReady.amount,
      fundsRequestedAmount: fundsRequested.amount,
      fundsReceivedAmount: fundsReceived.amount,
      paidAmount: paid.amount,
      pendingReconciliation: allStages['payment-initiated'].tasks,
      reconciliationRate: totalTasks > 0 ? Math.round((paid.tasks / totalTasks) * 100) : 0,
      conversionRate: totalTasks > 0 ? Math.round((paid.tasks / paymentReady.tasks) * 100) : 0,
    };
  }, [selectedCustomers, selectedProperties, selectedAccounts, selectedCommodities]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const stageConfig = {
    'payment-ready': {
      label: 'Payment Ready',
      icon: FileText,
      description: 'Bills ready for fund collection',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950/20',
      borderColor: 'border-blue-500',
    },
    'funds-requested': {
      label: 'Funds Requested',
      icon: Clock,
      description: 'Funds requested from customers',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 dark:bg-amber-950/20',
      borderColor: 'border-amber-500',
    },
    'funds-received': {
      label: 'Funds Received',
      icon: CheckCircle,
      description: 'Customer funds received and confirmed',
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-950/20',
      borderColor: 'border-green-500',
    },
    'payment-initiated': {
      label: 'Payment Initiated',
      icon: Play,
      description: 'Vendor payments initiated',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950/20',
      borderColor: 'border-purple-500',
    },
    'paid': {
      label: 'Paid',
      icon: FileCheck,
      description: 'Payments completed and reconciled',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950/20',
      borderColor: 'border-emerald-500',
    },
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Payments</h1>
            <p className="text-muted-foreground mt-2 text-lg">
              Stage-driven payment workflow with Payment Task ID tracking and reconciliation
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant={showUrgent ? 'default' : 'outline'} 
              size="sm" 
              className="gap-2"
              onClick={() => setShowUrgent(!showUrgent)}
            >
              <AlertTriangle className="h-4 w-4" />
              Urgent Payments
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
            <Button size="sm" className="gap-2">
              <Receipt className="h-4 w-4" />
              Create Payment Task
            </Button>
          </div>
        </div>
      </div>

      {/* Urgent Payments View */}
      {showUrgent && (
        <UrgentPaymentsView
          onClose={() => setShowUrgent(false)}
          customerFilter={selectedCustomers}
          accountFilter={selectedAccounts}
          commodityFilter={selectedCommodities}
        />
      )}

      {/* Overall Insights - Comparative Metrics */}
      <div className="space-y-4">
        <Card className="border-l-4 border-l-primary/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Payment Workflow Insights</CardTitle>
                <CardDescription>Comparative metrics across payment stages based on current filters</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Payment Ready vs Funds Requested */}
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground font-medium">Payment Ready</p>
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{overallInsights.paymentReady}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {overallInsights.paymentReadyBills} bills • {formatCurrency(overallInsights.paymentReadyAmount)}
                      </p>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground">Funds Requested</p>
                      <p className="text-lg font-semibold text-amber-600">{overallInsights.fundsRequested}</p>
                      <p className="text-xs text-muted-foreground">
                        {overallInsights.fundsRequestedBills} bills • {formatCurrency(overallInsights.fundsRequestedAmount)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Funds Received vs Paid */}
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground font-medium">Funds Received</p>
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{overallInsights.fundsReceived}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {overallInsights.fundsReceivedBills} bills • {formatCurrency(overallInsights.fundsReceivedAmount)}
                      </p>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground">Bills Paid</p>
                      <p className="text-lg font-semibold text-emerald-600">{overallInsights.paid}</p>
                      <p className="text-xs text-muted-foreground">
                        {overallInsights.paidBills} bills • {formatCurrency(overallInsights.paidAmount)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Conversion & Reconciliation Rate */}
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground font-medium">Conversion Rate</p>
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <TrendingUp className="h-4 w-4 text-purple-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-600">{overallInsights.conversionRate}%</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Ready → Paid
                      </p>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground">Reconciliation Rate</p>
                      <p className="text-lg font-semibold text-emerald-600">{overallInsights.reconciliationRate}%</p>
                      <p className="text-xs text-muted-foreground">
                        {overallInsights.paid} of {overallInsights.totalTasks} tasks
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Total Overview */}
              <Card className="border-l-4 border-l-primary">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground font-medium">Total Overview</p>
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Receipt className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{overallInsights.totalTasks}</p>
                      <p className="text-xs text-muted-foreground mt-1">Payment Tasks</p>
                    </div>
                    <div className="pt-2 border-t space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Total Bills</span>
                        <span className="text-sm font-semibold">{overallInsights.totalBills}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Total Amount</span>
                        <span className="text-sm font-semibold">{formatCurrency(overallInsights.totalAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Pending Recon.</span>
                        <span className="text-sm font-semibold text-amber-600">{overallInsights.pendingReconciliation}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Section */}
      <Card className="border-l-4 border-l-primary/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Filter className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Payment Filters</CardTitle>
              <CardDescription>Filter by customer, account, commodity, and Payment Task ID</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <MultiSelectFilter
              label="Customers"
              icon={<Users className="h-4 w-4 text-muted-foreground" />}
              options={customers.map((c: any) => ({ id: c.id, label: c.customer_name }))}
              selected={selectedCustomers}
              onSelectionChange={setSelectedCustomers}
              placeholder="All Customers"
            />
            <MultiSelectFilter
              label="Properties"
              icon={<Building className="h-4 w-4 text-muted-foreground" />}
              options={properties.map((p: any) => ({ id: p.id, label: p.property_name }))}
              selected={selectedProperties}
              onSelectionChange={setSelectedProperties}
              placeholder="All Properties"
            />
            <MultiSelectFilter
              label="Service Accounts"
              icon={<Home className="h-4 w-4 text-muted-foreground" />}
              options={accounts.slice(0, 100).map((a: any) => ({ 
                id: a.id, 
                label: `${a.account_number} (${a.commodity || 'N/A'})` 
              }))}
              selected={selectedAccounts}
              onSelectionChange={setSelectedAccounts}
              placeholder="All Accounts"
            />
            <MultiSelectFilter
              label="Commodities"
              icon={<Zap className="h-4 w-4 text-muted-foreground" />}
              options={commodities.map((c: string) => ({ 
                id: c, 
                label: c.charAt(0).toUpperCase() + c.slice(1) 
              }))}
              selected={selectedCommodities}
              onSelectionChange={setSelectedCommodities}
              placeholder="All Commodities"
            />
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Payment Task ID
              </Label>
              <Input
                type="text"
                value={selectedPaymentTaskId}
                onChange={(e) => setSelectedPaymentTaskId(e.target.value)}
                placeholder="Filter by Payment Task ID"
                className="font-mono"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stage Tabs */}
      <Tabs value={activeStage} onValueChange={(value) => setActiveStage(value as PaymentStage)} className="w-full">
        <Card className="border-l-4 border-l-primary/50">
          <CardContent className="p-0">
            <div className="border-b border-border bg-muted/30">
              <TabsList className="w-full h-auto p-2 bg-transparent justify-start overflow-x-auto flex-nowrap gap-2">
                {Object.entries(stageConfig).map(([stage, config]) => {
                  const Icon = config.icon;
                  const isActive = activeStage === stage;
                  const taskCounts = {
                    'payment-ready': 8,
                    'funds-requested': 5,
                    'funds-received': 6,
                    'payment-initiated': 3,
                    'paid': 2,
                  };
                  return (
                    <TabsTrigger
                      key={stage}
                      value={stage}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all whitespace-nowrap ${
                        isActive
                          ? `${config.bgColor} ${config.borderColor} border-2 shadow-md`
                          : 'border-transparent bg-background hover:bg-muted'
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${isActive ? config.color : 'text-muted-foreground'}`} />
                      <span className="hidden sm:inline">{config.label}</span>
                      <span className="sm:hidden">{config.label.split(' ')[0]}</span>
                      {isActive && (
                        <Badge variant="secondary" className="ml-1 text-xs">
                          {taskCounts[stage as keyof typeof taskCounts]}
                        </Badge>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          </CardContent>
        </Card>

        {/* Stage Content */}
        {Object.keys(stageConfig).map((stage) => (
          <TabsContent key={stage} value={stage} className="mt-6">
            <PaymentTaskList
              stage={stage as PaymentStage}
              customerFilter={selectedCustomers}
              propertyFilter={selectedProperties}
              accountFilter={selectedAccounts}
              commodityFilter={selectedCommodities}
              paymentTaskIdFilter={selectedPaymentTaskId}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
